// SellerWizard — den vertikala vägledningen på ai.qada.io.
//
// Sex steg, sekventiellt "current". Klar-status kommer från
// backend-service (compute_wizard_state) som läser deterministiskt
// från befintliga tabeller — ingen state persistas här på frontend.
//
// Varje steg har antingen:
//   - "Öppna X" → länk till broker-portalen (qada.io/valuation/... osv)
//   - "Fråga Sigge" → seedar chatt-input:et med sigge_prompt och
//     lämnar över till konversationsvyn
//
// Wizarden är ALLTID vägledande — säljaren kan hoppa mellan steg.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getSellerWizard } from "./api";


// Konventionellt broker-portal-host. På prod: ai.qada.io → qada.io
// för de sidor som ligger utanför AI-workspacet (värdering, IM Studio,
// säljarformulär). Kan sättas via env om ni deployer annorlunda.
function brokerPortalHost() {
  try {
    const h = window.location.hostname;
    if (h.startsWith("ai.")) return h.slice(3);   // ai.qada.io → qada.io
    return h;
  } catch (_e) {
    return "";
  }
}


function toBrokerUrl(hrefPath) {
  // Om href börjar med "/" och pekar på broker-portal-sida (/valuation,
  // /im-studio, /seller osv), bygg absolut URL till broker-hosten.
  // Om href är "/" eller AI-lokal path — låt vara.
  if (!hrefPath || hrefPath === "/") return null;
  const host = brokerPortalHost();
  if (!host) return null;
  const proto = window.location.protocol || "https:";
  return `${proto}//${host}${hrefPath}`;
}


export default function SellerWizard({ workspaceId, onSiggePrompt }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const s = await getSellerWizard(workspaceId);
      setState(s);
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte ladda vägledningen.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleSiggePrompt = useCallback((prompt) => {
    if (typeof onSiggePrompt === "function") {
      onSiggePrompt(prompt);
    } else {
      // Fallback: navigera till root med prompten som URL-state.
      navigate("/", { state: { initialUserMessage: prompt } });
    }
  }, [onSiggePrompt, navigate]);

  if (!workspaceId) return null;
  if (loading && !state) {
    return (
      <div className="qai-wizard">
        <div className="qai-wizard-header">Din väg genom Qada</div>
        <div className="qai-wizard-loading">Laddar…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="qai-wizard">
        <div className="qai-wizard-header">Din väg genom Qada</div>
        <div className="qai-wizard-error">{error}</div>
      </div>
    );
  }
  if (!state) return null;

  const { steps = [], completed_count: done = 0, total_count: total = 0 } = state;
  const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="qai-wizard">
      <div className="qai-wizard-header">
        <div className="qai-wizard-title">Din väg genom Qada</div>
        <div className="qai-wizard-progress">
          <div className="qai-wizard-progress-bar">
            <div
              className="qai-wizard-progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="qai-wizard-progress-text">
            {done} / {total} klara
          </span>
        </div>
      </div>

      <ol className="qai-wizard-steps">
        {steps.map((step) => (
          <WizardStep
            key={step.key}
            step={step}
            onSiggePrompt={handleSiggePrompt}
          />
        ))}
      </ol>

      <div className="qai-wizard-footer-note">
        Wizarden är vägledande. Sigge kan hjälpa dig med varje steg —
        klicka "Fråga Sigge" så börjar konversationen med rätt fråga.
        Inget genomförs automatiskt.
      </div>
    </div>
  );
}


function WizardStep({ step, onSiggePrompt }) {
  const {
    key, position, label, description, status,
    cta_label: ctaLabel, cta_href: ctaHref, sigge_prompt: siggePrompt,
    evidence,
  } = step;

  const brokerUrl = toBrokerUrl(ctaHref);
  const isComplete = status === "complete";
  const isCurrent = status === "current";
  const isLocked = status === "locked";

  return (
    <li
      className={
        "qai-wizard-step qai-wizard-step-" + status
      }
      aria-current={isCurrent ? "step" : undefined}
    >
      <div className="qai-wizard-step-marker">
        {isComplete ? (
          <span className="qai-wizard-step-check" aria-label="Klart">✓</span>
        ) : (
          <span className="qai-wizard-step-number">{position}</span>
        )}
      </div>
      <div className="qai-wizard-step-body">
        <div className="qai-wizard-step-title">{label}</div>
        <div className="qai-wizard-step-description">{description}</div>
        {isCurrent && evidence && (
          <EvidenceLine stepKey={key} evidence={evidence} />
        )}
        {!isLocked && (
          <div className="qai-wizard-step-actions">
            {brokerUrl ? (
              <a
                className="qai-wizard-btn qai-wizard-btn-primary"
                href={brokerUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {ctaLabel} ↗
              </a>
            ) : (
              <button
                type="button"
                className="qai-wizard-btn qai-wizard-btn-primary"
                onClick={() => onSiggePrompt(siggePrompt)}
              >
                {ctaLabel}
              </button>
            )}
            <button
              type="button"
              className="qai-wizard-btn qai-wizard-btn-ghost"
              onClick={() => onSiggePrompt(siggePrompt)}
            >
              Fråga Sigge
            </button>
          </div>
        )}
        {isLocked && (
          <div className="qai-wizard-step-locked-note">
            Låst tills föregående steg är klart. Du kan fortfarande
            hoppa in — status uppdateras automatiskt när kraven är
            uppfyllda.
          </div>
        )}
      </div>
    </li>
  );
}


function EvidenceLine({ stepKey, evidence }) {
  // Visa en kort human-readable evidens-rad för det aktuella steget
  // så säljaren vet exakt vad som återstår.
  let text = "";
  if (stepKey === "valuation_preliminary") {
    if (!evidence.has_preliminary && !evidence.has_finalized) {
      text = "Ingen värdering registrerad än.";
    } else if (evidence.equity_value_mid) {
      const m = evidence.equity_value_mid;
      text = `Preliminär mittvärdering: ${new Intl.NumberFormat("sv-SE").format(m)} SEK`;
    }
  } else if (stepKey === "dd_questionnaire") {
    const missing = evidence.questionnaire_missing_fields || [];
    const scored = evidence.risk_dimensions_scored || 0;
    const required = evidence.risk_dimensions_required || 4;
    if (missing.length > 0) {
      text = `Ägarformulär: ${missing.length} obligatoriskt fält kvar. `;
    }
    if (scored < required) {
      text += `Riskprofil: ${scored} av ${required} dimensioner skattade.`;
    }
    text = text || "Nästan klart.";
  } else if (stepKey === "im_finalized") {
    text = `${evidence.approved_sections || 0} av ${evidence.total_sections || 0} IM-sektioner godkända.`;
  } else if (stepKey === "teaser_approved") {
    text = evidence.latest_status
      ? `Senaste blänkare: status "${evidence.latest_status}".`
      : "Ingen blänkare skapad än.";
  } else if (stepKey === "buyer_shortlist") {
    text = `${evidence.outreach_drafts_approved_or_beyond || 0} godkända utkast, ${evidence.outreach_drafts_total || 0} totalt.`;
  } else if (stepKey === "first_outreach_sent") {
    text = `${evidence.outreach_drafts_sent || 0} utskick skickade, ${evidence.inbound_replies_received || 0} svar mottagna.`;
  }
  if (!text) return null;
  return <div className="qai-wizard-step-evidence">{text}</div>;
}
